import { useEffect, useRef, useState } from 'react'
import { Box, Group, Text, ActionIcon, Menu, Avatar, Divider, Indicator, Badge } from '@mantine/core'
import { useLocalStorage, useMediaQuery } from '@mantine/hooks'
import { IconArrowRight, IconBell, IconCheck, IconChevronDown, IconLogout, IconUser, IconMenu2, IconTicket } from '@tabler/icons-react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/store/auth'
import { handleLogout as performCoordinatedLogout } from '@/auth/handleLogout'
import { DarkModeToggle } from '@/components/ui/DarkModeToggle'
import { InstallAppButton } from '@/components/InstallAppButton'
import { IndicadorDeShells } from './IndicadorDeShells'
import { useNotificationsFeed } from '@/hooks/useNotificationsFeed'
import { notificationsFeedApi } from '@/api/notifications-feed'
import { chamadosApi } from '@/api/chamados'
import { ScopeAppliedBadge } from '@/components/chamados'
import type { ChamadoScope } from '@/hooks/useScope'

interface HeaderProps {
  title?: string
  onToggleSidebar?: () => void
}

export function Header({ title, onToggleSidebar }: HeaderProps) {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const isMobile = useMediaQuery('(max-width: 768px)')
  // TASK-2473: ScopeAppliedBadge condicional. Lemos URL diretamente em vez de
  // chamar useScope() para nao disparar useUserOrganizations em todas as rotas.
  // Hooks chamados sempre (regra de hooks); valor usado condicionalmente.
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const isInChamados = location.pathname.startsWith('/chamados')
  const headerScope = (searchParams.get('scope') as ChamadoScope | null) ?? 'own'

  const handleLogout = async () => {
    // CRITICO: NAO chamar setState({isAuthenticated: false}) ANTES do
    // performCoordinatedLogout — Router detectaria instantaneamente e
    // redirecionaria pra /login, que dispara oidc/start automaticamente,
    // chegando no broker /authorize ANTES de end_session ter revogado a
    // sessao broker. Resultado: re-login silencioso e usuario precisa
    // clicar Sair 2x.
    //
    // performCoordinatedLogout agora ordena: limpa localStorage, POST
    // /logout, AWAIT end_session (revoga cookie+denylist), depois
    // window.location='/login'. Reset do store eh redundante.
    await performCoordinatedLogout()
  }

  const displayName = (() => {
    const parts = (user?.full_name ?? '').trim().split(/\s+/).filter(Boolean)
    if (parts.length >= 2) return `${parts[0]} ${parts[parts.length - 1]}`
    if (parts.length === 1) return parts[0]
    return user?.email?.split('@')[0] ?? ''
  })()

  const initials = (() => {
    const parts = (user?.full_name ?? '').trim().split(/\s+/).filter(Boolean)
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return user?.email?.slice(0, 2).toUpperCase() ?? 'U'
  })()

  // Fase 4 (MR 3/5): sino agora le do feed unificado em vez do legacy
  // useNotifications. queryKey shape: ['notifications-feed', { status, limit }].
  // total_unread vem do backend (FeedResponse.total_unread).
  const qc = useQueryClient()
  const feedQuery = useNotificationsFeed({ status: 'unread', limit: 5 })
  const feedItems = feedQuery.data?.pages[0]?.items ?? []
  const unreadCount = feedQuery.data?.pages[0]?.total_unread ?? 0

  const [bellOpened, setBellOpened] = useState(false)

  // Inbox count cross-tenant — agrega chamados pendentes pra mostrar no sino.
  // Chave SÓ do Header: o menu tem a sua (`CHAVE_PENDENCIAS_DO_MENU`), porque aqui
  // a página é de 5 e lá a contagem é do servidor.
  const { data: inboxData } = useQuery({
    queryKey: ['chamados-inbox-count'],
    queryFn: async () => (await chamadosApi.getInbox({ limit: 5 })).data,
    refetchInterval: 30_000,
    staleTime: 15_000,
    enabled: !!user,
  })
  const inboxTotal = inboxData?.total ?? 0
  const inboxTopItems = inboxData?.items ?? []

  // Badge do sino = chamados pendentes nao-vistos. Estado "visto" via lista de
  // IDs no localStorage (namespaced por user). Apos 3s com dropdown aberto,
  // marca os IDs atuais como vistos — badge zera. Novo chamado que chegar
  // depois tem id fora do set => volta a incrementar.
  const [seenChamadoIds, setSeenChamadoIds] = useLocalStorage<number[]>({
    key: `notifications_seen_chamados:${user?.id ?? '__none__'}`,
    defaultValue: [],
    getInitialValueInEffect: false,
  })

  const inboxItemsRef = useRef(inboxTopItems)
  inboxItemsRef.current = inboxTopItems

  useEffect(() => {
    if (!bellOpened) return
    const timer = setTimeout(() => {
      setSeenChamadoIds(inboxItemsRef.current.map(c => c.id))
    }, 3000)
    return () => clearTimeout(timer)
  }, [bellOpened, setSeenChamadoIds])

  // - Sem user ou inboxTotal=0: badge zerado
  // - Primeira vez (nada marcado): mostra inboxTotal (sem cap do limit 5)
  // - Apos marcacao: conta items nao-vistos por id (capeado pelo limit fetchado)
  const unseenCount = (() => {
    if (!user || inboxTotal === 0) return 0
    if (seenChamadoIds.length === 0) return inboxTotal
    const seenSet = new Set(seenChamadoIds)
    return inboxTopItems.filter(c => !seenSet.has(c.id)).length
  })()

  const markReadMut = useMutation({
    mutationFn: (event_id: number) => notificationsFeedApi.markRead([event_id]),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications-feed'] }),
  })
  const markAllReadMut = useMutation({
    mutationFn: () => notificationsFeedApi.markAllRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications-feed'] }),
  })

  const formatTimeAgo = (dateStr: string) => {
    try {
      const diff = Date.now() - new Date(dateStr).getTime()
      const mins = Math.floor(diff / 60000)
      if (mins < 1) return 'agora'
      if (mins < 60) return `${mins}min`
      const hours = Math.floor(mins / 60)
      if (hours < 24) return `${hours}h`
      return `${Math.floor(hours / 24)}d`
    } catch { return '' }
  }

  return (
    <Box
      style={{
        height: 54,
        backgroundColor: 'var(--ai-header-bg)',
        borderBottom: '1px solid var(--ai-border-light)',
        padding: isMobile ? '0 12px' : '0 30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}
    >
      {/* Left: hamburger + title — minWidth:0 garante truncamento correto */}
      <Group gap={12} style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        {onToggleSidebar && (
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={onToggleSidebar}
            title="Alternar sidebar"
            style={{ flexShrink: 0 }}
          >
            <IconMenu2 size={18} />
          </ActionIcon>
        )}
        <Text
          fw={700}
          size="sm"
          c="var(--ai-text-primary)"
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {title ?? 'ConsoleAI'}
        </Text>
        {/* TASK-2473: badge de escopo aparece somente em rotas /chamados* */}
        {isInChamados && !isMobile && <ScopeAppliedBadge scope={headerScope} />}
      </Group>

      {/* Right: ações — flexShrink:0 para não ceder espaço ao título */}
      <Group gap={isMobile ? 6 : 10} style={{ flexShrink: 0 }}>
        {/* Some sozinho quando nao ha shell aberto. Ele existe para o custo de uma
            sessao viva nunca ficar invisivel — ver o comentario do componente. */}
        <IndicadorDeShells />
        <InstallAppButton compact={isMobile} />
        <DarkModeToggle />

        <Menu shadow="md" width={340} position="bottom-end" opened={bellOpened} onChange={setBellOpened}>
          <Menu.Target>
            <Indicator
              disabled={unseenCount === 0}
              label={unseenCount > 0 ? String(unseenCount) : undefined}
              size={16}
              color="red"
              offset={4}
            >
              <ActionIcon
                variant="subtle"
                color="gray"
                size="md"
                aria-label={
                  unseenCount > 0
                    ? `Chamados pendentes: ${unseenCount}`
                    : 'Chamados pendentes: nenhum'
                }
              >
                <IconBell size={18} />
              </ActionIcon>
            </Indicator>
          </Menu.Target>
          <Menu.Dropdown>
            {/* Seção: Chamados pendentes (cross-tenant via inbox) */}
            {inboxTotal > 0 && (
              <>
                <Menu.Label>
                  <Group justify="space-between">
                    <Group gap={6}>
                      <IconTicket size={14} />
                      <Text size="xs" fw={700} tt="uppercase">Chamados pendentes</Text>
                    </Group>
                    <Badge size="xs" color="red">{inboxTotal}</Badge>
                  </Group>
                </Menu.Label>
                {inboxTopItems.slice(0, 3).map((it) => (
                  <Menu.Item
                    key={`inbox-${it.id}`}
                    onClick={() => navigate(`/chamados/${it.id}`)}
                  >
                    <Box>
                      <Text size="xs" fw={600} lineClamp={1}>{it.title}</Text>
                      <Group gap={6} mt={2}>
                        <Text size="xs" c="dimmed">{it.tenant_name ?? '—'}</Text>
                        <Text size="xs" c="dimmed">·</Text>
                        <Text size="xs" c="dimmed">
                          {it.inbox_reason === 'assignee' && 'Atribuído a você'}
                          {it.inbox_reason === 'waiting_response' && 'Aguardando você'}
                          {it.inbox_reason === 'team' && (it.target_template_name ?? 'Time')}
                        </Text>
                      </Group>
                    </Box>
                  </Menu.Item>
                ))}
                <Menu.Item
                  onClick={() => navigate('/chamados/inbox')}
                  style={{ textAlign: 'center' }}
                >
                  <Text size="xs" c="inovareTeal" fw={500}>
                    Ver caixa de entrada{inboxTotal > 3 ? ` (${inboxTotal} no total)` : ''}
                  </Text>
                </Menu.Item>
                <Divider />
              </>
            )}

            {/* Seção: Notificações genéricas */}
            <Menu.Label>
              <Group justify="space-between">
                <Text size="xs" fw={700} tt="uppercase">Notificações</Text>
                {unreadCount > 0 && (
                  <Badge size="xs" color="red">{unreadCount} novas</Badge>
                )}
              </Group>
            </Menu.Label>
            {feedItems.length === 0 ? (
              <Box style={{ padding: '12px 16px', textAlign: 'center' }}>
                <Text size="xs" c="dimmed">
                  {inboxTotal === 0
                    ? 'Tudo em dia! Nenhuma notificação pendente.'
                    : 'Sem notificações genéricas.'}
                </Text>
              </Box>
            ) : (
              <>
                {feedItems.slice(0, 5).map(item => {
                  const isRead = item.read_at !== null
                  return (
                    <Menu.Item
                      key={item.event_id}
                      onClick={() => {
                        if (!isRead) markReadMut.mutate(item.event_id)
                        if (item.link) navigate(item.link)
                      }}
                    >
                      <Box>
                        <Text size="xs" fw={isRead ? 400 : 600}>{item.title}</Text>
                        {item.message && (
                          <Text size="xs" c="dimmed" lineClamp={1}>{item.message}</Text>
                        )}
                        <Text size="xs" c="dimmed" mt={2}>{formatTimeAgo(item.occurred_at)}</Text>
                      </Box>
                    </Menu.Item>
                  )
                })}
              </>
            )}

            {/* Footer actions — sempre visível (TASK-2578). "Marcar todas como
              lidas" continua condicional ao unreadCount; "Ver todas as
              notificações" sempre aparece, mesmo com 0 unread. */}
            <Divider />
            {unreadCount > 0 && (
              <Menu.Item onClick={() => markAllReadMut.mutate()} style={{ textAlign: 'center' }}>
                <Group gap={4} justify="center">
                  <IconCheck size={12} />
                  <Text size="xs" c="inovareTeal" fw={500}>Marcar todas como lidas</Text>
                </Group>
              </Menu.Item>
            )}
            <Menu.Item onClick={() => navigate('/notificacoes')} style={{ textAlign: 'center' }}>
              <Group gap={4} justify="center">
                <Text size="xs" c="inovareTeal" fw={500}>Ver todas as notificações</Text>
                <IconArrowRight size={12} />
              </Group>
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>

        <Menu shadow="md" width={200} position="bottom-end">
          <Menu.Target>
            <Group gap={8} style={{ cursor: 'pointer' }}>
              <Avatar size={30} radius="xl" color="inovareTeal" variant="filled" src={user?.avatar_url ?? undefined} styles={{ root: { fontSize: 12 } }}>
                {initials}
              </Avatar>
              {/* Em mobile, esconder nome/roles para evitar overflow no header */}
              {!isMobile && (
                <Box style={{ lineHeight: 1.2 }}>
                  <Text size="xs" fw={700} c="var(--ai-text-primary)" truncate maw={120}>
                    {displayName}
                  </Text>
                  <Text style={{ fontSize: 11 }} c="var(--ai-text-muted)">
                    {user?.roles?.length ? user.roles.map(r => r.name).join(', ') : (user?.role === 'admin' ? 'Admin' : 'Viewer')}
                  </Text>
                </Box>
              )}
              <IconChevronDown size={12} style={{ color: 'var(--ai-text-muted)' }} />
            </Group>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>{displayName}</Menu.Label>
            <Menu.Item leftSection={<IconUser size={14} />} onClick={() => navigate('/perfil')}>Perfil</Menu.Item>
            <Divider />
            <Menu.Item
              color="red"
              leftSection={<IconLogout size={14} />}
              onClick={handleLogout}
            >
              Sair
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Box>
  )
}
